import _ from "lodash";
import { encodeAwsJsonSourceHandle } from "./flowDiagramSourceHandle";

export type ProjectConflictTarget = "draft" | "published";
export type ProjectConflictOperation = "save" | "publish";
export type ProjectConflictDomain =
  | "connectors"
  | "experiments"
  | "data"
  | "branding"
  | "flow"
  | "hooks"
  | "projectInfo"
  | "playerSettings"
  | "thumbnail"
  | "dynamicThumbnails"
  | "miniSite"
  | "custom";

export type ProjectConflictField =
  | string
  | {
      path: string;
      domain?: ProjectConflictDomain;
      label?: string;
    };

export type ProjectConflict = {
  path: string;
  domain: ProjectConflictDomain;
  label: string;
};

export type ProjectConflictActor = {
  actor?: string | null;
  occurredAt?: string | null;
};

export type ProjectConflictCopy = {
  title: string;
  body: string;
  items: string[];
  footer: string;
  okText: string;
  cancelText: string;
};

type NormalizedField = {
  path: string;
  domain?: ProjectConflictDomain;
  label?: string;
};

const DOMAIN_LABELS: Record<ProjectConflictDomain, string> = {
  connectors: "connectors",
  experiments: "experiments",
  data: "live control data",
  branding: "branding",
  flow: "flow map",
  hooks: "custom hooks",
  projectInfo: "project information",
  playerSettings: "player settings",
  thumbnail: "thumbnail",
  dynamicThumbnails: "dynamic thumbnails",
  miniSite: "mini-site settings",
  custom: "project field",
};

const FIELD_DOMAIN_MAP: Record<string, ProjectConflictDomain> = {
  videoParts: "connectors",
  modsArr: "connectors",
  jsonUrl: "connectors",
  experiments: "experiments",
  experimentOptimizationTechnique: "experiments",
  settings: "data",
  settingsJsonSchemaStr: "data",
  stateJsonSchemaStr: "data",
  overlay: "branding",
  fontOverrides: "branding",
  flowDiagram: "flow",
  nodeLayout: "flow",
  customHooks: "hooks",
  playerSettings: "playerSettings",
  thumbS3Url: "thumbnail",
  dynamicThumbnails: "dynamicThumbnails",
  title: "projectInfo",
  description: "projectInfo",
  customHtml: "projectInfo",
  fileName: "projectInfo",
  minisiteConfigs: "miniSite",
  aliasId: "miniSite",
};

const FIELD_LABELS: Record<string, string> = {
  videoParts: "scene content",
  modsArr: "connectors",
  jsonUrl: "animation",
  settings: "settings",
  overlay: "overlay",
  fontOverrides: "fonts",
  flowDiagram: "flow map",
  nodeLayout: "flow map layout",
  customHooks: "custom hooks",
  playerSettings: "player settings",
  title: "project title",
  description: "project description",
  stateJsonSchemaStr: "per-video data",
  settingsJsonSchemaStr: "live control data",
  thumbS3Url: "thumbnail",
  customHtml: "custom HTML",
  fileName: "file name",
  aliasId: "mini-site alias",
  minisiteConfigs: "mini-site settings",
  allowSdkConnect: "SDK connection setting",
  allowCrmConnect: "CRM connection setting",
  allowDataConnect: "data connection setting",
  allowCreateDataPoint: "data point creation setting",
  allowSendFormData: "form submission setting",
};

const CHANGE_NOUNS: Record<string, string> = {
  animation: "animation changes",
  connectors: "connector changes",
  experiments: "experiment changes",
  settings: "setting changes",
  "branding settings": "branding setting changes",
  overlay: "overlay changes",
  fonts: "font changes",
  branding: "branding changes",
  "flow map": "flow map changes",
  "flow map layout": "flow map layout changes",
  "custom hooks": "custom hook changes",
  "project title": "project title changes",
  "project description": "project description changes",
  "custom HTML": "custom HTML changes",
  "file name": "file name changes",
  "player settings": "player settings changes",
  thumbnail: "thumbnail changes",
  "dynamic thumbnails": "dynamic thumbnail changes",
  "mini-site settings": "mini-site setting changes",
  "mini-site alias": "mini-site alias changes",
  "live control data": "live control data",
  "per-video data": "per-video data",
  "player version": "player version changes",
  "font map": "font map changes",
  scenes: "scene changes",
  "scene names": "scene name changes",
  "scene description": "scene description changes",
  "scene content": "scene content changes",
};

const PLURAL_LABELS = new Set([
  "connectors",
  "experiments",
  "fonts",
  "custom hooks",
  "player settings",
  "dynamic thumbnails",
  "mini-site settings",
  "scenes",
  "scene names",
]);

function normalizeField(field: ProjectConflictField): NormalizedField {
  if (typeof field === "string") {
    return { path: field };
  }

  return field;
}

function firstPathSegment(path: string): string {
  return path.split(".").find(Boolean) || path;
}

function normalizeSnapshotValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeSnapshotValue);
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).reduce(
      (result, [key, nestedValue]) => {
        if (key === "__typename") {
          return result;
        }

        result[key] =
          key === "sourceHandle"
            ? encodeAwsJsonSourceHandle(nestedValue)
            : normalizeSnapshotValue(nestedValue);
        return result;
      },
      {} as Record<string, unknown>
    );
  }

  return value;
}

export function normalizeConflictSnapshot<T>(value: T): T {
  return normalizeSnapshotValue(value) as T;
}

export function humanizeConflictField(path: string): string {
  const field = firstPathSegment(path);
  const explicitLabel = FIELD_LABELS[field];
  if (explicitLabel) {
    return explicitLabel;
  }

  return field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\bsdk\b/g, "SDK")
    .replace(/\bcrm\b/g, "CRM")
    .replace(/\bhtml\b/g, "HTML")
    .replace(/\bid\b/g, "ID");
}

export function getConflictFieldLabel(field: ProjectConflictField): string {
  const normalized = normalizeField(field);
  if (normalized.label) {
    return normalized.label;
  }

  const topLevelField = firstPathSegment(normalized.path);
  const explicitLabel = FIELD_LABELS[topLevelField];
  if (explicitLabel) {
    return explicitLabel;
  }

  const domain = normalized.domain || FIELD_DOMAIN_MAP[topLevelField];
  if (domain && domain !== "custom") {
    return DOMAIN_LABELS[domain];
  }

  return humanizeConflictField(normalized.path);
}

export function getConflictFieldDomain(
  field: ProjectConflictField
): ProjectConflictDomain {
  const normalized = normalizeField(field);
  const topLevelField = firstPathSegment(normalized.path);
  return normalized.domain || FIELD_DOMAIN_MAP[topLevelField] || "custom";
}

function getComparableValue(snapshot: unknown, path: string): unknown {
  const topLevelField = firstPathSegment(path);
  return _.get(snapshot, path, _.get(snapshot, topLevelField));
}

export function detectProjectConflicts(params: {
  baseline?: unknown;
  latest?: unknown;
  next?: unknown;
  fields: ProjectConflictField[];
}): ProjectConflict[] {
  const { baseline, latest, next, fields } = params;
  if (!baseline || !latest || fields.length === 0) {
    return [];
  }

  const conflictsByLabel = new Map<string, ProjectConflict>();

  fields.forEach((field) => {
    const normalized = normalizeField(field);
    const path = normalized.path;
    const baselineValue = normalizeConflictSnapshot(
      getComparableValue(baseline, path)
    );
    const latestValue = normalizeConflictSnapshot(
      getComparableValue(latest, path)
    );
    const nextValue = normalizeConflictSnapshot(getComparableValue(next, path));

    const latestChangedSinceBaseline = !_.isEqual(baselineValue, latestValue);
    const outgoingAlreadyMatchesLatest =
      typeof next !== "undefined" && _.isEqual(latestValue, nextValue);

    if (!latestChangedSinceBaseline || outgoingAlreadyMatchesLatest) {
      return;
    }

    const label = getConflictFieldLabel(normalized);
    if (!conflictsByLabel.has(label)) {
      conflictsByLabel.set(label, {
        path,
        domain: getConflictFieldDomain(normalized),
        label,
      });
    }
  });

  return Array.from(conflictsByLabel.values());
}

function formatActor(actor?: string | null, capitalize = false): string {
  const value = actor || "someone";
  if (!capitalize) {
    return value;
  }

  return value === "someone" ? "Someone" : value;
}

export function formatConflictRelativeTime(dateString?: string | null): string {
  if (!dateString) {
    return "";
  }

  const timestamp = new Date(dateString).getTime();
  if (!Number.isFinite(timestamp)) {
    return "";
  }

  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 45) {
    return " just now";
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return ` ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return ` ${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(hours / 24);
  return ` ${days} day${days === 1 ? "" : "s"} ago`;
}

function getTargetDescription(target: ProjectConflictTarget): string {
  return target === "published" ? "live" : "draft";
}

function getMutationVerb(params: {
  operation: ProjectConflictOperation;
  target: ProjectConflictTarget;
}): string {
  if (params.operation === "publish") {
    return "published";
  }

  return "saved";
}

function getActionVerb(operation: ProjectConflictOperation): string {
  return operation === "publish" ? "Publishing" : "Saving";
}

function getOverwriteVerb(operation: ProjectConflictOperation): string {
  return operation === "publish" ? "replace" : "overwrite";
}

function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getChangeNoun(label: string): string {
  return CHANGE_NOUNS[label] || `${label} changes`;
}

function getConflictItemVerb(label: string): "was" | "were" {
  return PLURAL_LABELS.has(label) ? "were" : "was";
}

export function buildProjectConflictCopy(params: {
  conflicts: ProjectConflict[];
  target: ProjectConflictTarget;
  operation: ProjectConflictOperation;
  actor?: ProjectConflictActor;
}): ProjectConflictCopy {
  const { conflicts, target, operation, actor } = params;
  const actionVerb = getActionVerb(operation);
  const overwriteVerb = getOverwriteVerb(operation);
  const targetDescription = getTargetDescription(target);
  const mutationVerb = getMutationVerb({ operation, target });
  const relativeTime = formatConflictRelativeTime(actor?.occurredAt);
  const actorName = formatActor(actor?.actor);

  if (conflicts.length === 1) {
    const [conflict] = conflicts;
    const noun = getChangeNoun(conflict.label);
    return {
      title: "Newer changes found",
      body: `${formatActor(
        actor?.actor,
        true
      )} ${mutationVerb} ${targetDescription} changes to ${
        conflict.label
      }${relativeTime}. ${actionVerb} now will ${overwriteVerb} those ${noun}.`,
      items: [],
      footer: "",
      okText:
        operation === "publish" ? "Override and publish" : "Override and save",
      cancelText: "Cancel",
    };
  }

  return {
    title: "Newer changes found",
    body: `There are newer changes that overlap with what you're about to ${
      operation === "publish" ? "publish" : "save"
    }:`,
    items: conflicts.map(
      (conflict) =>
        `${capitalizeFirst(conflict.label)} ${getConflictItemVerb(
          conflict.label
        )} ${mutationVerb} by ${actorName}${relativeTime}`
    ),
    footer: `${actionVerb} now will ${overwriteVerb} these changes.`,
    okText:
      operation === "publish" ? "Override and publish" : "Override and save",
    cancelText: "Cancel",
  };
}
